import { AlertTriangle, CursorBox, Feather } from "@untitledui/icons";
import { useState } from "react";
import { Tab, TabList, Tabs } from "@/components/application/tabs/tabs";
import { Button } from "@/components/base/buttons/button";
import { PageLayout } from "@/components/page-layout";
import { useWorkspaceAdmin } from "@/hooks/use-workspace-admin";
import { cx } from "@/utils/cx";
import { LAYOUT_CONSTRAINTS } from "@/utils/layout-constants";
import { WorkspaceConfigureModal } from "./WorkspaceConfigureModal";
import { WorkspaceDefaultsBanner } from "./WorkspaceDefaultsBanner";
import { WorkspacePoliciesTab } from "./WorkspacePoliciesTab";
import { WorkspacePolicyDetail } from "./WorkspacePolicyDetail";
import { WorkspaceRisksExportButton } from "./WorkspaceRisksExportButton";
import { WorkspaceRisksTab } from "./WorkspaceRisksTab";

type TabId = "risks" | "policies";

const tabs = [
  { id: "risks", label: "Risks", icon: <AlertTriangle className="w-4 h-4" /> },
  { id: "policies", label: "Policies", icon: <Feather className="w-4 h-4" /> },
] satisfies { id: TabId; label: string; icon: React.ReactNode }[];

// The workspace default safety template (UNB-1946). A Workspace Admin sets the
// risks new merchants inherit when they onboard into this workspace. Access itself
// is gated by `WorkspaceLayout` (UNB-2694) — this only mounts once that's confirmed.
export const WorkspaceSafetyConfig = () => {
  const { workspace } = useWorkspaceAdmin();
  const [activeTab, setActiveTab] = useState<TabId>("risks");
  const [modalOpen, setModalOpen] = useState(false);
  // Set while a single policy's steps are open, replacing the tabs in place.
  const [openPolicyId, setOpenPolicyId] = useState<string | null>(null);

  if (!workspace) return null;

  const workspaceId = workspace.id;

  if (openPolicyId) {
    return (
      // Held to the standard page width, with the same cream background the
      // merchant policy detail bleeds to the full viewport.
      <div className="flex flex-col items-center w-full min-h-screen bg-unbox-cream-30">
        <div className={cx(LAYOUT_CONSTRAINTS.CONTAINER_WIDTH, "flex flex-col p-8")}>
          <WorkspacePolicyDetail
            workspaceId={workspaceId}
            policyId={openPolicyId}
            onBack={() => setOpenPolicyId(null)}
          />
        </div>
      </div>
    );
  }

  const tabContent: Record<TabId, React.ReactNode> = {
    risks: <WorkspaceRisksTab workspaceId={workspaceId} />,
    policies: <WorkspacePoliciesTab workspaceId={workspaceId} onOpenPolicy={setOpenPolicyId} />,
  };

  return (
    <PageLayout
      title="Default safety settings"
      subtitle="These are your workspace defaults. New merchants will inherit these on onboarding but can edit them."
      icon={CursorBox}
      backgroundColor="#FFFCF9"
      callToAction={
        <div className="flex items-center gap-3">
          <WorkspaceRisksExportButton workspaceId={workspaceId} workspaceName={workspace.name} />
          <Button color="primary" size="sm" onClick={() => setModalOpen(true)}>
            Configure default risks
          </Button>
        </div>
      }
    >
      <WorkspaceDefaultsBanner />

      <div className="flex-shrink-0">
        <Tabs selectedKey={activeTab} onSelectionChange={(key) => setActiveTab(key as TabId)}>
          <TabList type="underline" items={tabs}>
            {(tab) => <Tab key={tab.id} id={tab.id} label={tab.label} icon={tab.icon} />}
          </TabList>
        </Tabs>
      </div>

      {tabContent[activeTab]}

      <WorkspaceConfigureModal workspaceId={workspaceId} open={modalOpen} onOpenChange={setModalOpen} />
    </PageLayout>
  );
};
